import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Phone, BookOpen, Menu, X, LogOut, LogIn, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/books", label: "Books" },
  { to: "/assignments", label: "Assignments" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const nav = useNavigate();

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-neutral-200/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <span className="w-9 h-9 rounded-full bg-[#E05A47] flex items-center justify-center">
            <BookOpen size={18} className="text-white" />
          </span>
          <div className="leading-tight">
            <div className="font-serif text-xl font-semibold tracking-tight text-[#0F172A]">Chand Book Depot</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500">Rohini, Delhi · Since Decades</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#E05A47] font-medium" : "text-neutral-700 hover:text-[#E05A47]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user && user.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-[#4A6741] font-medium" : "text-neutral-700 hover:text-[#4A6741]"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-open-btn"
            onClick={() => setCartOpen(true)}
            aria-label="Open cart"
            className="relative p-2 rounded-full hover:bg-neutral-100 transition-colors"
            style={{ transitionProperty: "background-color" }}
          >
            <ShoppingCart size={18} className="text-[#0F172A]"/>
            {count > 0 && (
              <span
                data-testid="cart-badge"
                className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#E05A47] text-white text-[10px] font-medium flex items-center justify-center"
              >
                {count > 9 ? "9+" : count}
              </span>
            )}
          </button>
          <a
            href="tel:09211813521"
            data-testid="call-now-cta"
            className="hidden md:inline-flex items-center gap-2 bg-[#0F172A] hover:bg-[#E05A47] text-white px-4 py-2 rounded-full text-sm transition-colors"
            style={{ transitionProperty: "background-color, color" }}
          >
            <Phone size={14} />
            Call Now
          </a>
          {user && user.role === "admin" ? (
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={() => { logout(); nav("/"); }}
              className="rounded-full"
            >
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          ) : (
            <Link
              to="/login"
              data-testid="admin-login-link"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-[#0F172A]"
            >
              <LogIn size={14} /> Admin
            </Link>
          )}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 rounded-md hover:bg-neutral-100"
            aria-label="Toggle menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className="text-neutral-700 hover:text-[#E05A47] py-1"
              >
                {l.label}
              </NavLink>
            ))}
            {user && user.role === "admin" && (
              <NavLink to="/admin" onClick={() => setOpen(false)} className="text-[#4A6741] py-1">Admin</NavLink>
            )}
            {!user && (
              <Link to="/login" onClick={() => setOpen(false)} className="text-neutral-700 py-1">Admin Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
